"use client"

import { useState } from "react"
import Link from "next/link"
import Image from "next/image"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Linkedin, Mail, Phone, MapPin } from "lucide-react"
import { submitNewsletterForm } from "@/app/actions/submit-form"
import { toast } from "sonner"

export function Footer() {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const formData = new FormData(e.currentTarget)
      const result = await submitNewsletterForm({ success: false, message: '' }, formData)

      if (result.success) {
        toast.success(result.message)
        e.currentTarget.reset()
      } else {
        toast.error(result.message)
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <footer className="bg-black text-white relative overflow-hidden">
      {/* Decorative blurred shapes */}
      <div className="pointer-events-none absolute -top-20 -right-24 h-64 w-64 rounded-full bg-[#1a85ca]/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#34D1BE]/20 blur-3xl" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center md:text-left">
          {/* Company Info */}
          <div className="space-y-4 col-span-2 md:col-span-1 flex flex-col items-center md:items-start">
            <div className="bg-transparent rounded-lg p-0 w-fit">
              <Image
                src="/images/Octohealth-logo1.png"
                alt="OctoHealth Logo"
                width={170}
                height={46}
                className="h-11 w-auto"
              />
            </div>
            <p className="text-gray-400">
              Transform your operations with one intelligent platform that automates underwriting, claims, authorizations, and pharmacy benefits all in real time.
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-center md:justify-start space-x-2 text-gray-400">
                <Mail className="h-4 w-4" />
                <span>sales@octosoft.ai</span>
              </div>
              <div className="flex items-center justify-center md:justify-start space-x-2 text-gray-400">
                <Phone className="h-4 w-4" />
                <span>+234 (0) 201 330 2766</span>
              </div>
              <div className="flex items-center justify-center md:justify-start space-x-2 text-gray-400">
                <MapPin className="h-4 w-4" />
                <span>Lagos, Nigeria</span>
              </div>
            </div>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-semibold mb-4 text-white">Legal</h3>
            <ul className="space-y-3 text-gray-400">
              <li>
                <Link href="/privacy-policy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="/terms-of-use" className="hover:text-white transition-colors">
                  Terms & Condition
                </Link>
              </li>
              <li>
                <Link href="/cookie-policy" className="hover:text-white transition-colors">
                  Cookie Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4 text-white">Quick Links</h3>
            <ul className="space-y-3 text-gray-400">
              <li>
                <Link href="/blog" className="hover:text-white transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link href="#about" className="hover:text-white transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link href="/contact-us" className="hover:text-white transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter & Social */}
          <div className="col-span-2 md:col-span-1 flex flex-col items-center md:items-start">
            <h3 className="font-semibold mb-4 text-white">Stay Connected</h3>
            <p className="text-gray-400 mb-4 text-center md:text-left">
              Get the latest healthcare insights and product updates.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2 mb-6 w-full max-w-sm">
              <Input
                name="email"
                type="email"
                required
                placeholder="Your email"
                className="bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder:text-gray-300 focus:ring-2 focus:ring-[#1a85ca]"
              />
              <Button type="submit" disabled={isSubmitting} className="bg-[#1a85ca] hover:bg-[#1a85cc] shadow-lg transition-all">
                {isSubmitting ? "Subscribing..." : "Subscribe"}
              </Button>
            </form>

            {/* Social Media Icons (LinkedIn only) */}
            <div>
              <h4 className="font-medium mb-3 text-white text-center md:text-left">Follow Us</h4>
              <div className="flex space-x-4 justify-center md:justify-start">
                <Link
                  href="https://www.linkedin.com/company/octohealth-erp" target="_blank"
                  className="p-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg hover:bg-[#1a85ca] transition-colors group"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5 text-gray-300 group-hover:text-white" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-center md:text-left">© {new Date().getFullYear()} OctoHealth. All rights reserved.</p>
            <div className="flex flex-wrap justify-center md:justify-end gap-x-6">
            {/*  <Link href="#" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link href="#" className="text-gray-400 hover:text-white transition-colors">
                Terms of Service
              </Link>
              <Link href="#" className="text-gray-400 hover:text-white transition-colors">
                Cookie Policy
              </Link>
              <Link href="#" className="text-gray-400 hover:text-white transition-colors">
                Legal
              </Link> */}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
